import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  useTheme,
  Chip,
} from '@material-ui/core';
import { MdPictureAsPdf, MdImage, MdInsertDriveFile, MdDescription } from 'react-icons/md';
import { Formulario, Campo } from '../../../interfaces/form-flow';

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  formData: Formulario[];
  allValues: Record<string, any>;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  open,
  onClose,
  formData,
  allValues,
}) => {
  const theme = useTheme();
  const sortedForms = [...formData].sort((a, b) => a.ordem - b.ordem);


  const stats = useMemo(() => {
    let totalFields = 0;
    let filledFields = 0;
    let requiredFields = 0;
    let requiredFilled = 0;

    sortedForms.forEach(form => {
      form.campos.forEach(field => {
        totalFields++;
        const fieldIdStr = String(field.id);
        const fieldIdNum = Number(field.id);
        const value = allValues[fieldIdStr] || allValues[fieldIdNum] || allValues[field.id];
        const isFilled = value !== undefined && value !== null && value !== '';
        
        if (isFilled) {
          filledFields++;
        }
        
        if (field.obrigatorio) {
          requiredFields++;
          if (isFilled) {
            requiredFilled++;
          }
        }
      });
    });

    return { totalFields, filledFields, requiredFields, requiredFilled };
  }, [sortedForms, allValues]);

  const stripHTML = (html: string): string => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };


  const getFileIcon = (fileName: string, fileType?: string): JSX.Element => {
    const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : '';
    const type = fileType?.toLowerCase() || extension || '';
    
    if (type.includes('pdf') || extension === 'pdf') {
      return <MdPictureAsPdf style={{ fontSize: 24, color: '#d32f2f' }} />;
    }
    if (type.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
      return <MdImage style={{ fontSize: 24, color: '#1976d2' }} />;
    }
    if (type.includes('word') || ['doc', 'docx'].includes(extension || '')) {
      return <MdDescription style={{ fontSize: 24, color: '#1976d2' }} />;
    }
    return <MdInsertDriveFile style={{ fontSize: 24, color: '#757575' }} />;
  };

  const renderFieldValue = (field: Campo, value: any) => {
    const isEmpty = value === undefined || value === null || (value === '' && field.tipo !== 'Area');
    const isRequiredEmpty = field.obrigatorio && isEmpty;
    
    if (isEmpty) {
      return (
        <Typography 
          variant="body2" 
          component="div" 
          style={{ 
            color: isRequiredEmpty ? '#d32f2f' : '#999', 
            fontStyle: 'italic',
            fontWeight: isRequiredEmpty ? 500 : 'normal'
          }}
        >
          {isRequiredEmpty ? '⚠️ Campo obrigatório não preenchido' : 'Não preenchido'}
        </Typography>
      );
    }

    switch (field.tipo) {
      case 'Texto':
      case 'Numero':
      case 'Email':
      case 'Telefone':
      case 'Phone':
        return <Typography variant="body2" component="div" style={{ color: '#333' }}>{String(value)}</Typography>;
      case 'Arquivo':
        if (value && typeof value === 'object') {
          if (value.url || value.nome || value.id) {
            const fileName = value.nome || value.name || 'Arquivo anexado';
            const fileExtension = fileName && fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : '';
            const fileType = value.type || fileExtension || '';
            return (
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {getFileIcon(fileName, fileType)}
                <Typography variant="body2" component="span" style={{ color: '#333' }}>
                  {fileName}
                </Typography>
              </Box>
            );
          }
          if (value.name) {
            const fileExtension = value.name.includes('.') ? value.name.split('.').pop()?.toLowerCase() : '';
            const fileType = value.type || fileExtension || '';
            return (
              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {getFileIcon(value.name, fileType)}
                <Typography variant="body2" component="span" style={{ color: '#333' }}>
                  {value.name}
                </Typography>
              </Box>
            );
          }
        }
        if (value instanceof File) {
          const fileExtension = value.name.includes('.') ? value.name.split('.').pop()?.toLowerCase() : '';
          const fileType = value.type || fileExtension || '';
          return (
            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {getFileIcon(value.name, fileType)}
              <Typography variant="body2" component="span" style={{ color: '#333' }}>
                {value.name}
              </Typography>
            </Box>
          );
        }
        return (
          <Typography variant="body2" component="div" style={{ color: '#999', fontStyle: 'italic' }}>
            Nenhum arquivo selecionado
          </Typography>
        );

      case 'Multiplos_Arquivos':
        if (Array.isArray(value) && value.length > 0) {
          return (
            <Box>
              {value.map((file: any, index: number) => {
                const fileName = file.nome || file.name || `Arquivo ${index + 1}`;
                const fileExtension = fileName && fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() : '';
                const fileType = file.type || fileExtension || '';
                
                return (
                  <Box key={file.id || index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: index < value.length - 1 ? '8px' : '0' }}>
                    {getFileIcon(fileName, fileType)}
                    <Typography variant="body2" component="span" style={{ color: '#333' }}>
                      {fileName}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        }
        if (value instanceof FileList) {
          return (
            <Box>
              {Array.from(value).map((file: File, index: number) => {
                const fileExtension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : '';
                const fileType = file.type || fileExtension || '';
                return (
                  <Box key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: index < value.length - 1 ? '8px' : '0' }}>
                    {getFileIcon(file.name, fileType)}
                    <Typography variant="body2" component="span" style={{ color: '#333' }}>
                      {file.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        }
        return (
          <Typography variant="body2" component="div" style={{ color: '#999', fontStyle: 'italic' }}>
            Nenhum arquivo selecionado
          </Typography>
        );

      case 'Alternativas':
      case 'Alternativas_API':
      case 'Municipio':
      case 'Instituicao_Ensino':
      case 'Modalidade':
      case 'Multiplos':
        if (Array.isArray(value)) {
          return <Typography variant="body2" component="div">{value.join(', ')}</Typography>;
        }
        if (value && typeof value === 'object' && (value.label !== undefined || value.titulo !== undefined)) {
          return (
            <Typography variant="body2" component="div">
              {String(value.label ?? value.titulo)}
            </Typography>
          );
        }
        return <Typography variant="body2" component="div">{String(value)}</Typography>;

      case 'Area':
        return (
          <Typography variant="body2" component="div" style={{ whiteSpace: 'pre-wrap' }}>
            {String(value)}
          </Typography>
        );

      case 'Data':
        if (value instanceof Date) {
          return <Typography variant="body2" component="div">{value.toLocaleDateString('pt-BR')}</Typography>;
        }
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
          return <Typography variant="body2" component="div">{new Date(value).toLocaleDateString('pt-BR')}</Typography>;
        }
        return <Typography variant="body2" component="div">{String(value)}</Typography>;

      default:
        return <Typography variant="body2" component="div">{String(value)}</Typography>;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }
      }}
    >
      <Box
        style={{
          backgroundColor: theme.palette.primary.main,
          color: '#fff',
          padding: '20px 24px',
        }}
      >
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <Box>
            <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              Pré Visualização
            </Typography>
            <Typography variant="body2" style={{ opacity: 0.9 }}>
              Verifique os dados antes de avançar.
            </Typography>
          </Box>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
            <Chip 
              label={`${stats.filledFields}/${stats.totalFields} campos preenchidos`}
              size="small"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                color: '#fff',
                fontWeight: 500
              }}
            />
            {stats.requiredFields > 0 && (
              <Chip 
                label={`${stats.requiredFilled}/${stats.requiredFields} obrigatórios`}
                size="small"
                style={{ 
                  backgroundColor: stats.requiredFilled === stats.requiredFields 
                    ? 'rgba(76, 175, 80, 0.3)' 
                    : 'rgba(211, 47, 47, 0.3)', 
                  color: '#fff',
                  fontWeight: 500
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <DialogContent dividers style={{ flex: 1, overflow: 'auto', scrollBehavior: 'smooth' }}>
        {sortedForms.map((form, formIndex) => (
          <Box key={form.slug} style={{ marginBottom: formIndex < sortedForms.length - 1 ? 32 : 0 }}>
            <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 'bold', color: '#264259' }}>
              {stripHTML(form.titulo)}
            </Typography>
            {form.descricao && (
              <Typography variant="body2" style={{ marginBottom: 16, color: '#666' }}>
                {stripHTML(form.descricao)}
              </Typography>
            )}
            <Grid container spacing={2}>
              {form.campos
                .sort((a, b) => a.ordem - b.ordem)
                .map((field) => {
                  const fieldIdStr = String(field.id);
                  const fieldIdNum = Number(field.id);
                  let fieldValue = allValues[fieldIdStr];
                  if (fieldValue === undefined) {
                    fieldValue = allValues[fieldIdNum];
                  }
                  if (fieldValue === undefined) {
                    fieldValue = allValues[field.id];
                  }
                  
                  return (
                    <Grid item xs={12} key={field.id}>
                      <Box
                        style={{
                          padding: '8px 12px',
                          backgroundColor: fieldValue === undefined || fieldValue === null || (fieldValue === '' && field.tipo !== 'Area')
                            ? (field.obrigatorio ? '#ffebee' : '#f8f9fa')
                            : '#f8f9fa',
                          borderRadius: '4px',
                          border: fieldValue === undefined || fieldValue === null || (fieldValue === '' && field.tipo !== 'Area')
                            ? (field.obrigatorio ? '1px solid #ffcdd2' : '1px solid #dee2e6')
                            : '1px solid #dee2e6',
                          marginBottom: '6px',
                        }}
                      >
                        <Box style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                          <Typography variant="subtitle2" component="span" style={{ fontWeight: 'bold', color: '#264259' }}>
                            {stripHTML(field.titulo)}:
                          </Typography>
                          {field.obrigatorio && (
                            <span style={{ color: 'red', fontSize: '14px' }}>*</span>
                          )}
                        </Box>
                        <Box style={{ marginLeft: '0px' }}>
                          {renderFieldValue(field, fieldValue)}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
            </Grid>
            {formIndex < sortedForms.length - 1 && (
              <Box style={{ borderTop: '2px solid #dee2e6', marginTop: 24, paddingTop: 24 }} />
            )}
          </Box>
        ))}
      </DialogContent>

      <Box
        style={{
          backgroundColor: theme.palette.primary.main,
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button 
          onClick={onClose} 
          variant="contained"
          style={{
            backgroundColor: '#fff',
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            minWidth: '100px',
          }}
        >
          OK
        </Button>
      </Box>
    </Dialog>
  );
};

export default PreviewModal;

